import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/CloudUpload';
import { useAppContext } from '../../context/AppContext';

const DocumentUpload = () => {
  const { uploadDocument, topics, ragStrategies, selectedStrategy, loading } = useAppContext();
  
  const [file, setFile] = useState(null);
  const [topicId, setTopicId] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [useExistingTopic, setUseExistingTopic] = useState(true);
  const [ragStrategy, setRagStrategy] = useState(selectedStrategy);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && !selectedFile.name.toLowerCase().endsWith('.pdf')) {
      setError('Only PDF files are accepted.');
      setFile(null);
      return;
    }
    setFile(selectedFile);
    setError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validate inputs
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    
    const finalTopicId = useExistingTopic ? topicId : newTopic;
    if (!finalTopicId) {
      setError('Please provide a topic ID.');
      return;
    }
    
    // Create form data
    const formData = new FormData();
    formData.append('file', file);
    formData.append('topic_id', finalTopicId);
    if (documentId) formData.append('document_id', documentId);
    if (ragStrategy) formData.append('rag_strategy_name', ragStrategy);
    
    try {
      const response = await uploadDocument(formData);
      setSuccess(`Document uploaded successfully! Processing started in the background.`);
      
      // Reset form
      setFile(null);
      setDocumentId('');
      // Don't reset topic to allow for multiple uploads to same topic
      
      // Reset file input by clearing the value
      const fileInput = document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload document. Please try again.');
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Upload Document
      </Typography>
      <Divider sx={{ mb: 3 }} />
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            fullWidth
          >
            {file ? file.name : 'Select PDF File'}
            <input
              id="file-upload"
              type="file"
              accept=".pdf"
              hidden
              onChange={handleFileChange}
            />
          </Button>
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Topic Selection
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant={useExistingTopic ? "contained" : "outlined"}
              onClick={() => setUseExistingTopic(true)}
              size="small"
            >
              Existing Topic
            </Button>
            <Button
              variant={!useExistingTopic ? "contained" : "outlined"}
              onClick={() => setUseExistingTopic(false)}
              size="small"
            >
              New Topic
            </Button>
          </Box>
          
          {useExistingTopic ? (
            <FormControl fullWidth>
              <InputLabel id="topic-select-label">Topic</InputLabel>
              <Select
                labelId="topic-select-label"
                value={topicId}
                label="Topic"
                onChange={(e) => setTopicId(e.target.value)}
                required
              >
                {topics.map((topic) => (
                  <MenuItem key={topic} value={topic}>
                    {topic}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              fullWidth
              label="New Topic ID"
              value={newTopic}
              onChange={(e) => setNewTopic(e.target.value)}
              required
              helperText="Create a new topic ID (e.g., 'neonatal_jaundice')"
            />
          )}
        </Box>
        
        <TextField
          fullWidth
          label="Document ID (Optional)"
          value={documentId}
          onChange={(e) => setDocumentId(e.target.value)}
          margin="normal"
          helperText="Leave blank to use filename as document ID"
        />
        
        <FormControl fullWidth margin="normal">
          <InputLabel id="rag-strategy-label">RAG Strategy</InputLabel>
          <Select
            labelId="rag-strategy-label"
            value={ragStrategy}
            label="RAG Strategy"
            onChange={(e) => setRagStrategy(e.target.value)}
          >
            {ragStrategies.map((strategy) => (
              <MenuItem key={strategy.id} value={strategy.id}>
                {strategy.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3 }}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Uploading...' : 'Upload Document'}
        </Button>
      </Box>
    </Paper>
  );
};

export default DocumentUpload;
